import { CSSProperties, useState } from 'react';
import { Editor } from '@tiptap/react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover.tsx';
import { applyFontSize, getActiveFontSize } from './fontSize.ts';

interface TextSizeOption {
  id: string;
  label: string;
  fontSize: string | null; // null = editor default
  previewStyle: CSSProperties;
}

const TEXT_SIZE_OPTIONS: TextSizeOption[] = [
  {
    id: 'xs',
    label: 'Tekst bardzo mały',
    fontSize: '10px',
    previewStyle: { fontSize: '10px' },
  },
  {
    id: 'sm',
    label: 'Tekst mały',
    fontSize: '12px',
    previewStyle: { fontSize: '12px' },
  },
  {
    id: 'base',
    label: 'Tekst',
    fontSize: null,
    previewStyle: { fontSize: '14px' },
  },
  {
    id: 'lg',
    label: 'Tekst duży',
    fontSize: '18px',
    previewStyle: { fontSize: '18px' },
  },
  {
    id: 'xl',
    label: 'Tekst bardzo duży',
    fontSize: '24px',
    previewStyle: { fontSize: '20px' },
  },
];

const FONT_SIZE_MIN = 8;
const FONT_SIZE_MAX = 64;

const getCurrentSizeOption = (editor: Editor): TextSizeOption => {
  const fontSize = getActiveFontSize(editor) ?? null;
  return (
    TEXT_SIZE_OPTIONS.find((option) => option.fontSize === fontSize) ??
    TEXT_SIZE_OPTIONS.find((option) => option.fontSize === null)!
  );
};

const parseFontSizePx = (value: string | undefined): number | '' => {
  if (!value) return '';
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : '';
};

const applyCustomFontSize = (editor: Editor, value: string) => {
  if (!value) {
    applyFontSize(editor, null);
    return;
  }
  const numeric = parseInt(value, 10);
  if (!Number.isFinite(numeric)) return;
  const clamped = Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, numeric));
  applyFontSize(editor, `${clamped}px`);
};

export const TextStyleDropdown = ({ editor }: { editor: Editor }) => {
  const [open, setOpen] = useState(false);
  const current = getCurrentSizeOption(editor);
  const currentFontSizePx = parseFontSizePx(getActiveFontSize(editor));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Rozmiar tekstu"
          className="flex h-8 min-w-[150px] items-center justify-between gap-1 rounded-md px-2 text-sm text-neutral-700 transition hover:bg-neutral-50"
        >
          <span className="truncate">{current.label}</span>
          <ChevronDown size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[200px] p-1">
        {TEXT_SIZE_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              applyFontSize(editor, option.fontSize);
              setOpen(false);
            }}
            style={option.previewStyle}
            className={cn(
              'flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-left text-sm font-medium text-neutral-900 hover:bg-neutral-50',
              current.id === option.id && 'bg-neutral-50',
            )}
          >
            {option.label}
          </button>
        ))}
        <div className="my-1 h-px bg-neutral-100" />
        <div className="flex items-center gap-2 px-2 py-1.5">
          <span className="text-sm text-neutral-700">Rozmiar:</span>
          <input
            type="number"
            min={FONT_SIZE_MIN}
            max={FONT_SIZE_MAX}
            defaultValue={currentFontSizePx}
            placeholder="14"
            onBlur={(e) => applyCustomFontSize(editor, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
                setOpen(false);
              }
            }}
            className="h-7 w-16 rounded-md border border-neutral-200 px-1.5 text-sm outline-none focus:border-neutral-400"
          />
          <span className="text-sm text-neutral-500">px</span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
